import { useState, useMemo } from 'react';
import { sessionsAPI } from '../../../lib/api';
import api from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Checkbox } from '../../ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Clock, Calendar as CalendarIcon, Mic2, Search, GripVertical } from 'lucide-react';

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ─── Sortable session row ─── */
const SortableSessionRow = ({ session, getSpeakerNames, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: session.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${isDragging ? 'bg-primary/5 border-primary/30' : 'bg-white/5 border-white/10'}`}
      data-testid={`session-item-${session.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 -ml-1">
          <GripVertical size={16} className="text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock size={12} /> {session.start_time || '--:--'} - {session.end_time || '--:--'}
            </span>
            {session.room && <span className="text-xs text-muted-foreground/70">{session.room}</span>}
          </div>
          <h4 className="font-medium">{session.title}</h4>
          {session.speaker_ids?.length > 0 && (
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
              <Mic2 size={12} /> {getSpeakerNames(session.speaker_ids)}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 ml-3 flex-shrink-0">
        <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(session)} data-testid={`edit-session-${session.id}`}><Pencil size={14} /></Button>
        <Button type="button" variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => onDelete(session.id)} data-testid={`delete-session-${session.id}`}><Trash2 size={14} /></Button>
      </div>
    </div>
  );
};

/* ─── Sortable track chip (for reordering track tab order) ─── */
const SortableTrackTab = ({ track, count, isSelected, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: track.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center" data-testid={`track-tab-${track.id}`}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 mr-1">
        <GripVertical size={12} className="text-muted-foreground/50" />
      </div>
      <button type="button" onClick={() => onSelect(track.id)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
          isSelected
            ? 'bg-white/15 border border-white/20 shadow-sm'
            : 'bg-white/5 border border-transparent hover:bg-white/10'
        }`}>
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: track.color || '#6366f1' }} />
        <span className="truncate">{track.name}</span>
        <span className={`text-xs ${isSelected ? 'text-white/70' : 'text-muted-foreground'}`}>({count})</span>
      </button>
    </div>
  );
};

/* ─── Speaker row (used in session dialog) ─── */
const SpeakerRow = ({ speaker, checked, onToggle, getSpeakerFullName }) => (
  <label className="flex items-center gap-3 p-2 rounded hover:bg-white/10 cursor-pointer transition-colors">
    <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`session-speaker-${speaker.id}`} />
    <div className="flex items-center gap-3 flex-1 min-w-0">
      {speaker.image ? (
        <img src={speaker.image.startsWith('http') ? speaker.image : `${process.env.REACT_APP_BACKEND_URL}${speaker.image}`} alt={getSpeakerFullName(speaker)} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0"><Mic2 size={14} className="text-primary" /></div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm truncate">{getSpeakerFullName(speaker)}</p>
        {(speaker.role || speaker.company) && <p className="text-xs text-muted-foreground truncate">{[speaker.role, speaker.company].filter(Boolean).join(' · ')}</p>}
      </div>
    </div>
  </label>
);

/* ─── Main component ─── */
export const AgendaTab = ({ editingEvent, sessions, setSessions, tracks, speakers }) => {
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [speakerSearchQuery, setSpeakerSearchQuery] = useState('');
  const [sessionFormData, setSessionFormData] = useState({
    title: '', track_id: '', speaker_ids: [], description: '', start_time: '', end_time: '',
  });
  const [trackOrder, setTrackOrder] = useState(editingEvent?.track_order || []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Build ordered track list from event sessions
  const orderedTracks = useMemo(() => {
    const eventSessions = sessions.filter(s => s.event_id === editingEvent?.id);
    if (eventSessions.length === 0) return [];

    const knownTrackIds = new Set(tracks.map(t => t.id));
    const eventTrackIds = new Set();
    let hasUnassigned = false;
    eventSessions.forEach(s => {
      if (s.track_id && knownTrackIds.has(s.track_id)) eventTrackIds.add(s.track_id);
      else hasUnassigned = true;
    });

    const usedTracks = tracks.filter(t => eventTrackIds.has(t.id));
    if (hasUnassigned) usedTracks.push({ id: '__unassigned__', name: 'Senza Track', color: '#6b7280' });
    if (usedTracks.length === 0) return [];

    const order = trackOrder.length > 0 ? trackOrder : editingEvent?.track_order || [];
    const ordered = [];
    order.forEach(tid => { const t = usedTracks.find(x => x.id === tid); if (t) ordered.push(t); });
    usedTracks.forEach(t => { if (!ordered.find(x => x.id === t.id)) ordered.push(t); });
    return ordered;
  }, [tracks, sessions, editingEvent, trackOrder]);

  // Sessions grouped by track
  const sessionsByTrack = useMemo(() => {
    const eventSessions = sessions.filter(s => s.event_id === editingEvent?.id);
    const knownTrackIds = new Set(tracks.map(t => t.id));
    const grouped = {};
    orderedTracks.forEach(t => { grouped[t.id] = []; });
    eventSessions.forEach(s => {
      const tid = (s.track_id && knownTrackIds.has(s.track_id)) ? s.track_id : '__unassigned__';
      if (!grouped[tid]) grouped[tid] = [];
      grouped[tid].push(s);
    });
    Object.values(grouped).forEach(arr => {
      arr.sort((a, b) => {
        if ((a.order ?? 999) !== (b.order ?? 999)) return (a.order ?? 999) - (b.order ?? 999);
        return (a.start_time || '').localeCompare(b.start_time || '');
      });
    });
    return grouped;
  }, [sessions, editingEvent, orderedTracks, tracks]);

  // Auto-select first track
  const activeTrackId = selectedTrackId && orderedTracks.find(t => t.id === selectedTrackId)
    ? selectedTrackId
    : orderedTracks[0]?.id || null;

  const activeSessions = activeTrackId ? (sessionsByTrack[activeTrackId] || []) : [];
  const totalSessions = Object.values(sessionsByTrack).flat().length;

  const getSpeakerFullName = (s) => s ? ([s.first_name, s.last_name].filter(Boolean).join(' ') || s.name || '') : '';
  const getSpeakerNames = (ids) => !ids?.length ? '-' : ids.map(id => getSpeakerFullName(speakers.find(s => s.id === id))).filter(Boolean).join(', ');

  const resetSessionForm = () => {
    setSessionFormData({ title: '', track_id: '', speaker_ids: [], description: '', start_time: '', end_time: '' });
    setEditingSession(null); setSpeakerSearchQuery('');
  };

  const openNew = () => {
    resetSessionForm();
    if (activeTrackId && activeTrackId !== '__unassigned__') {
      setSessionFormData(prev => ({ ...prev, track_id: activeTrackId }));
    }
    setSessionDialogOpen(true);
  };
  const openEdit = (session) => {
    setEditingSession(session);
    setSessionFormData({
      title: session.title || '', track_id: session.track_id || '',
      speaker_ids: session.speaker_ids || [], description: session.description || '',
      start_time: session.start_time || '', end_time: session.end_time || '',
    });
    setSessionDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;
    try {
      const data = { ...sessionFormData, event_id: editingEvent.id };
      if (editingSession) { await sessionsAPI.update(editingSession.id, data); toast.success('Sessione aggiornata'); }
      else { await sessionsAPI.create(data); toast.success('Sessione creata'); }
      const res = await sessionsAPI.getAll();
      setSessions(res.data);
      setSessionDialogOpen(false); resetSessionForm();
    } catch { toast.error('Errore nel salvataggio della sessione'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa sessione?')) return;
    try { await sessionsAPI.delete(id); setSessions(sessions.filter(s => s.id !== id)); toast.success('Sessione eliminata'); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const toggleSpeaker = (id) => {
    setSessionFormData(prev => ({
      ...prev, speaker_ids: prev.speaker_ids.includes(id) ? prev.speaker_ids.filter(x => x !== id) : [...prev.speaker_ids, id],
    }));
  };

  /* ─── Drag handlers ─── */
  const handleTrackDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedTracks.findIndex(t => t.id === active.id);
    const newIndex = orderedTracks.findIndex(t => t.id === over.id);
    const newOrder = arrayMove(orderedTracks, oldIndex, newIndex);
    setTrackOrder(newOrder.map(t => t.id));
    try {
      await api.put(`/events/${editingEvent.id}/track-order`, { track_order: newOrder.map(t => t.id) });
    } catch { toast.error("Errore nell'aggiornamento ordine track"); }
  };

  const handleSessionDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const items = [...activeSessions];
    const oldIndex = items.findIndex(s => s.id === active.id);
    const newIndex = items.findIndex(s => s.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    const updates = reordered.map((s, i) => ({ id: s.id, order: i }));
    setSessions(prev => {
      const next = [...prev];
      updates.forEach(u => { const s = next.find(x => x.id === u.id); if (s) s.order = u.order; });
      return next;
    });
    try { await api.put('/sessions/reorder', updates); }
    catch { toast.error("Errore nell'aggiornamento ordine sessioni"); }
  };

  const filteredSpeakers = speakers.filter(s => {
    if (!speakerSearchQuery.trim()) return true;
    const q = speakerSearchQuery.toLowerCase();
    const name = getSpeakerFullName(s).toLowerCase();
    return name.includes(q) || s.first_name?.toLowerCase().includes(q) || s.last_name?.toLowerCase().includes(q) || s.role?.toLowerCase().includes(q) || s.company?.toLowerCase().includes(q);
  });

  if (!editingEvent) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <CalendarIcon className="mx-auto mb-4 opacity-50" size={48} />
        <p>Salva prima l'evento per gestire l'agenda</p>
      </div>
    );
  }

  if (orderedTracks.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon className="text-primary" size={20} />
            <div>
              <h3 className="font-medium">Agenda Evento</h3>
              <p className="text-xs text-muted-foreground">0 sessioni</p>
            </div>
          </div>
          <Button type="button" size="sm" onClick={openNew} data-testid="add-session-btn">
            <Plus size={16} className="mr-1" /> Nuova Sessione
          </Button>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <CalendarIcon className="mx-auto mb-4 opacity-50" size={48} />
          <p>Nessuna sessione per questo evento</p>
          <p className="text-sm">Clicca "Nuova Sessione" per aggiungerne una</p>
        </div>
        {renderSessionDialog()}
      </div>
    );
  }

  function renderSessionDialog() {
    return (
      <Dialog open={sessionDialogOpen} onOpenChange={(open) => { setSessionDialogOpen(open); if (!open) resetSessionForm(); }}>
        <DialogContent className="max-w-lg bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{editingSession ? 'Modifica Sessione' : 'Nuova Sessione'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input value={sessionFormData.title} onChange={(e) => setSessionFormData({ ...sessionFormData, title: e.target.value })} required placeholder="es. Keynote di apertura" className="bg-white/5 border-white/10" data-testid="session-input-title" />
            </div>
            <div className="space-y-2">
              <Label>Track *</Label>
              <Select value={sessionFormData.track_id} onValueChange={(v) => setSessionFormData({ ...sessionFormData, track_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="session-select-track"><SelectValue placeholder="Seleziona track" /></SelectTrigger>
                <SelectContent>
                  {tracks.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color || '#6366f1' }} />{t.name}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Orario Inizio *</Label>
                <Input type="time" value={sessionFormData.start_time} onChange={(e) => setSessionFormData({ ...sessionFormData, start_time: e.target.value })} required className="bg-white/5 border-white/10" data-testid="session-input-start-time" />
              </div>
              <div className="space-y-2">
                <Label>Orario Fine *</Label>
                <Input type="time" value={sessionFormData.end_time} onChange={(e) => setSessionFormData({ ...sessionFormData, end_time: e.target.value })} required className="bg-white/5 border-white/10" data-testid="session-input-end-time" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea value={sessionFormData.description} onChange={(e) => setSessionFormData({ ...sessionFormData, description: e.target.value })} placeholder="Descrizione della sessione..." className="bg-white/5 border-white/10 min-h-[80px]" data-testid="session-input-description" />
            </div>
            <div className="space-y-2">
              <Label>Speaker</Label>
              <div className="relative mb-2">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input type="text" placeholder="Cerca speaker..." value={speakerSearchQuery} onChange={(e) => setSpeakerSearchQuery(e.target.value)} className="bg-white/5 border-white/10 pl-9" data-testid="session-speaker-search" />
              </div>

              {/* Assigned speakers */}
              {(() => {
                const assigned = filteredSpeakers.filter(s => sessionFormData.speaker_ids.includes(s.id));
                const available = filteredSpeakers.filter(s => !sessionFormData.speaker_ids.includes(s.id));
                return (
                  <>
                    {assigned.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-primary px-1">Assegnati ({assigned.length})</p>
                        <div className="space-y-1 p-2 rounded-lg bg-primary/5 border border-primary/20">
                          {assigned.map((speaker) => (
                            <SpeakerRow key={speaker.id} speaker={speaker} checked={true}
                              onToggle={() => toggleSpeaker(speaker.id)} getSpeakerFullName={getSpeakerFullName} />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="space-y-1">
                      {assigned.length > 0 && <p className="text-xs font-medium text-muted-foreground px-1 mt-2">Disponibili ({available.length})</p>}
                      <div className="max-h-40 overflow-y-auto space-y-1 p-2 rounded-lg bg-white/5 border border-white/10">
                        {available.length > 0 ? available.map((speaker) => (
                          <SpeakerRow key={speaker.id} speaker={speaker} checked={false}
                            onToggle={() => toggleSpeaker(speaker.id)} getSpeakerFullName={getSpeakerFullName} />
                        )) : (
                          <p className="text-sm text-muted-foreground text-center py-3">{speakerSearchQuery ? 'Nessun risultato' : (assigned.length > 0 ? 'Tutti gli speaker sono assegnati' : 'Nessuno speaker disponibile')}</p>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button type="button" variant="outline" onClick={() => setSessionDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="save-session-btn">{editingSession ? 'Salva Modifiche' : 'Crea Sessione'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarIcon className="text-primary" size={20} />
          <div>
            <h3 className="font-medium">Agenda Evento</h3>
            <p className="text-xs text-muted-foreground">{totalSessions} sessioni · {orderedTracks.length} track</p>
          </div>
        </div>
        <Button type="button" size="sm" onClick={openNew} data-testid="add-session-btn">
          <Plus size={16} className="mr-1" /> Nuova Sessione
        </Button>
      </div>

      {/* Track tabs (draggable for reorder, clickable for filter) */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTrackDragEnd}>
        <SortableContext items={orderedTracks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-wrap gap-1.5" data-testid="track-tabs">
            {orderedTracks.map(track => (
              <SortableTrackTab
                key={track.id}
                track={track}
                count={(sessionsByTrack[track.id] || []).length}
                isSelected={track.id === activeTrackId}
                onSelect={setSelectedTrackId}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Sessions for selected track */}
      {activeSessions.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSessionDragEnd}>
          <SortableContext items={activeSessions.map(s => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {activeSessions.map(session => (
                <SortableSessionRow key={session.id} session={session}
                  getSpeakerNames={getSpeakerNames} onEdit={openEdit} onDelete={handleDelete} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="text-center py-8 text-muted-foreground border border-dashed border-white/10 rounded-lg">
          <p className="text-sm">Nessuna sessione in questa track</p>
          <p className="text-xs mt-1">Clicca "Nuova Sessione" per aggiungerne una</p>
        </div>
      )}

      {renderSessionDialog()}
    </div>
  );
};
